<template>
  <div class="builder-component-col" :style="col.style">
    <template v-if="col.type === 'text'">
      <TextBase v-bind="col.props" />
    </template>
    <template v-else-if="col.type === 'button'">
      <ButtonPrimary
        v-if="col.props.palette === 'primary'"
        :size="col.props.size"
        class="button"
        :buttonText="col.props.label"
        :tabIndex="col.props.tabIndex"
        @click="$emit(col.props.emit)"
      />
      <ButtonSecondary
        v-else-if="col.props.palette === 'secondary'"
        :size="col.props.size"
        class="button"
        :buttonText="col.props.label"
        :tabIndex="col.props.tabIndex"
        @click="$emit(col.props.emit)"
      />
      <div v-else>Button not implemented</div>
    </template>
    <template v-else-if="col.type === 'color-circle'">
      <div class="color-circle" :style="{ backgroundColor: col.props.color }"></div>
    </template>
    <template v-else-if="col.type === 'divider'">
      <DividerHorizontal />
    </template>
    <template v-else-if="col.type === 'component'">
      <component :is="col.props.is" />
    </template>
    <template v-else-if="col.type === 'image'">
      <img class="image" :src="col.props.src" />
    </template>
    <template v-else-if="col.type === 'builder'">
      <BuilderComponent :rows="col.props.rows" v-bind="$attrs" />
    </template>
  </div>
</template>

<script lang="ts" setup>
import DividerHorizontal from '@/design-system/atoms/DividerHorizontal.vue'
import TextBase, { Props as TextBaseProps } from '@/design-system/atoms/TextBase.vue'
import ButtonPrimary from '@/design-system/atoms/buttons/ButtonPrimary.vue'
import ButtonSecondary from '@/design-system/atoms/buttons/ButtonSecondary.vue'
import { CSSProperties } from 'vue'
import BuilderComponent, { Row } from './BuilderComponent.vue'

export type ColumnType =
  | 'text'
  | 'color-circle'
  | 'button'
  | 'divider'
  | 'component'
  | 'image'
  | 'builder'

export type ColumnPropsT<Type extends ColumnType> = Type extends 'text'
  ? TextBaseProps
  : Type extends 'divider'
  ? object
  : Type extends 'component'
  ? {
      is: string
    }
  : Type extends 'builder'
  ? {
      rows: Row[]
    }
  : Type extends 'image'
  ? {
      src: string
    }
  : Type extends 'color-circle'
  ? {
      color: string
    }
  : Type extends 'button'
  ? {
      label: string
      size: 'small' | 'medium' | 'large'
      palette: 'primary' | 'secondary' | 'success' | 'warning' | 'error'
      tabIndex?: number
      emit: string
    }
  : never

type ColumnAdditional = {
  style?: CSSProperties
}

type PropType<Type extends ColumnType> = {
  type: Type
  props: ColumnPropsT<Type>
}

export type ColumnT<Type extends ColumnType> = (Type extends 'text'
  ? PropType<'text'>
  : Type extends 'color-circle'
  ? PropType<'color-circle'>
  : Type extends 'button'
  ? PropType<'button'>
  : Type extends 'divider'
  ? PropType<'divider'>
  : Type extends 'component'
  ? PropType<'component'>
  : Type extends 'builder'
  ? PropType<'builder'>
  : Type extends 'image'
  ? PropType<'image'>
  : never) &
  ColumnAdditional

export type Column = ColumnT<ColumnType>

type Props = {
  col: Column
}

defineProps<Props>()

defineEmits<{
  (event: string): void
}>()
</script>

<style scoped lang="scss">
.builder-component-col {
  display: flex;
  flex-direction: column;
  margin: 0;

  .button {
    width: 100%;
  }

  .color-circle {
    width: 100%;
    height: 100%;
    border-radius: 50%;
  }

  .image {
    width: 100%;
    height: 100%;
  }
}
</style>
